// Copyright (c) 2026 Aravind Adari. All rights reserved.

import { useState, useEffect, useMemo } from "react";
import {
  getDocs,
  query,
  where,
  Timestamp,
} from "firebase/firestore";
import { useClient } from "../contexts/ClientContext";
import { timed, perfTotal } from "../utils/perf";
import { getEarnedHolidays, getFYMonths, getQuarters, getHalfYears } from "../utils/reportUtils";
import {
  Box,
  Button,
  Typography,
  TextField,
  Paper,
  Snackbar,
  Alert,
  Divider,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  TableContainer,
  Tabs,
  Tab,
  LinearProgress,
  Chip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  SelectChangeEvent,
  TablePagination,
} from "@mui/material";
import AssessmentIcon from "@mui/icons-material/Assessment";
import DownloadIcon from "@mui/icons-material/Download";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import DateRangeIcon from "@mui/icons-material/DateRange";
import PeopleIcon from "@mui/icons-material/People";
import SummaryCard from "../components/SummaryCard";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import dayjs, { Dayjs } from "dayjs";
import usePagination from "../hooks/usePagination";

/* ─── Types ─── */
interface Employee {
  id: string;
  empId: string;
  firstName: string;
  lastName: string;
  fullName: string;
  designation: string;
  baseSalary: number;
  status: string;
  branchId: string;
  cashAmount: number;
  allowances: Record<string, number>;
  tdAllowancePct: number;
}
interface AttendanceRow {
  empId: string;
  date: string;
  clockIn: Timestamp | null;
  clockOut: Timestamp | null;
  verified: boolean;
}
interface LeaveRow {
  empId: string;
  startDate: string;
  endDate: string;
  type: string;
  status: string;
}
interface LocationItem {
  id: string;
  name: string;
}


export default function ReportsPage() {
  const { col } = useClient();
  const currentMonth = dayjs().month(); // 0-based
  const defaultFYStart = currentMonth >= 3 ? dayjs().year() : dayjs().year() - 1; // Apr=3

  const [fyStart, setFyStart] = useState(defaultFYStart);
  const [tab, setTab] = useState(0); // 0=Monthly, 1=Quarterly, 2=Half-yearly, 3=Annual
  const [employees, setEmployees] = useState<Employee[]>([]);
  const [attendance, setAttendance] = useState<AttendanceRow[]>([]);
  const [leaveRequests, setLeaveRequests] = useState<LeaveRow[]>([]);
  const [locations, setLocations] = useState<LocationItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [msg, setMsg] = useState("");
  const [locationFilter, setLocationFilter] = useState("");
  const [useCustomRange, setUseCustomRange] = useState(false);
  const [rangeStart, setRangeStart] = useState("");
  const [rangeEnd, setRangeEnd] = useState("");

  const fyLabel = `FY ${fyStart}-${(fyStart + 1).toString().slice(2)}`;
  const fyStartDate = `${fyStart}-04-01`;
  const fyEndDate = `${fyStart + 1}-03-31`;

  const effectiveStartDate = useCustomRange && rangeStart ? rangeStart : fyStartDate;
  const effectiveEndDate = useCustomRange && rangeEnd ? rangeEnd : fyEndDate;

  useEffect(() => {
    async function loadData() {
      setLoading(true);
      const t0 = performance.now();
      try {
        const [empSnap, attSnap, leaveSnap, locSnap] = await Promise.all([
          timed("ReportsPage.load.employees", () => getDocs(col("employees"))),
          timed(`ReportsPage.load.attendance(${effectiveStartDate}→${effectiveEndDate})`, () => getDocs(query(col("attendance"), where("date", ">=", effectiveStartDate), where("date", "<=", effectiveEndDate)))),
          timed("ReportsPage.load.leaveRequests", () => getDocs(col("leaveRequests"))),
          timed("ReportsPage.load.branches", () => getDocs(col("branches"))),
        ]);
        setEmployees(empSnap.docs.map((d) => {
          const data = d.data() as Record<string, unknown>;
          return {
            id: d.id,
            empId: (data.empId as string) ?? d.id,
            firstName: (data.firstName as string) ?? "",
            lastName: (data.lastName as string) ?? "",
            fullName: (data.fullName as string) ?? "",
            designation: (data.designation as string) ?? (data.role as string) ?? "",
            baseSalary: Number(data.baseSalary ?? 0),
            status: (data.status as string) ?? "active",
            branchId: (data.branchId as string) ?? "",
            cashAmount: Number(data.cashAmount ?? 0),
            allowances: (data.allowances as Record<string, number>) ?? {},
            tdAllowancePct: Number(data.tdAllowancePct ?? 10),
          };
        }));
        setAttendance(attSnap.docs.map((d) => d.data() as AttendanceRow));
        setLeaveRequests(leaveSnap.docs.map((d) => d.data() as LeaveRow));
        setLocations(locSnap.docs.map((d) => ({ id: d.id, name: (d.data().name as string) ?? "" })).sort((a, b) => {
          if (a.name.toLowerCase().startsWith("anakapalli")) return -1;
          if (b.name.toLowerCase().startsWith("anakapalli")) return 1;
          return a.name.localeCompare(b.name);
        }));
        perfTotal("ReportsPage.load TOTAL", t0);
      } catch {
        setMsg("Failed to load report data.");
      } finally {
        setLoading(false);
      }
    }
    loadData();
    // eslint-disable-next-line
  }, [fyStart, effectiveStartDate, effectiveEndDate]);

  const getLocName = (id: string) => locations.find((l) => l.id === id)?.name ?? "—";
  const getEmpName = (e: Employee) =>
    e.firstName && e.lastName ? `${e.firstName} ${e.lastName}` : e.fullName || e.empId;

  const filteredEmployees = useMemo(() => {
    const active = employees.filter((e) => e.status === "active");
    return locationFilter ? active.filter((e) => e.branchId === locationFilter) : active;
  }, [employees, locationFilter]);

  // Generate period data
  function getPeriodData(start: string, end: string) {
    return filteredEmployees.map((emp) => {
      const empAtt = attendance.filter(
        (a) => (a.empId === emp.id || a.empId === emp.empId) && a.date >= start && a.date <= end
      );
      const workDays = new Set(empAtt.map((a) => a.date)).size;
      const empLeaves = leaveRequests.filter(
        (l) =>
          l.empId === emp.id &&
          l.status === "approved" &&
          l.startDate <= end &&
          l.endDate >= start
      );
      const leaveDays = empLeaves.reduce((s, l) => {
        const ls = l.startDate < start ? start : l.startDate;
        const le = l.endDate > end ? end : l.endDate;
        return s + dayjs(le).diff(dayjs(ls), "day") + 1;
      }, 0);

      const earnedHolidays = getEarnedHolidays(workDays);

      // Calculate total allowances for the period
      const totalAllowances = Object.values(emp.allowances).reduce((s, v) => s + v, 0);

      // Estimate salary for period
      const months = dayjs(end).diff(dayjs(start), "month") + 1;
      const daysInMonth = dayjs(start).daysInMonth();
      const totalDays = workDays + earnedHolidays;
      const dailyRate = emp.baseSalary / daysInMonth;
      const totalPaidDays = totalDays;
      const basePay = dailyRate * totalPaidDays;
      const grossEstimate = basePay * (1 + emp.tdAllowancePct / 100) + (totalAllowances * months);

      return {
        empId: emp.empId || emp.id,
        empName: getEmpName(emp),
        designation: emp.designation,
        location: getLocName(emp.branchId),
        workDays,
        leaveDays,
        earnedHolidays,
        baseSalary: emp.baseSalary,
        totalAllowances: totalAllowances * months,
        grossEstimate,
        cashEstimate: Math.min(emp.cashAmount * months, grossEstimate),
        onlineEstimate: Math.max(grossEstimate - emp.cashAmount * months, 0),
      };
    });
  }

  const periods = useMemo(() => {
    switch (tab) {
      case 0:
        return getFYMonths(fyStart);
      case 1:
        return getQuarters(fyStart);
      case 2:
        return getHalfYears(fyStart);
      case 3:
        return [{ label: fyLabel, start: fyStartDate, end: fyEndDate }];
      default:
        return [];
    }
  }, [tab, fyStart, fyLabel, fyStartDate, fyEndDate]);

  const [selectedPeriod, setSelectedPeriod] = useState(0);

  useEffect(() => {
    setSelectedPeriod(0);
  }, [tab]);

  const currentPeriod = periods[selectedPeriod] ?? periods[0];
  const periodData = useMemo(() => {
    if (!currentPeriod) return [];
    return getPeriodData(currentPeriod.start, currentPeriod.end);
    // eslint-disable-next-line
  }, [currentPeriod, filteredEmployees, attendance, leaveRequests]);

  const periodTotals = useMemo(
    () => periodData.reduce(
      (acc, d) => ({
        workDays: acc.workDays + d.workDays,
        leaveDays: acc.leaveDays + d.leaveDays,
        grossEstimate: acc.grossEstimate + d.grossEstimate,
        cashEstimate: acc.cashEstimate + d.cashEstimate,
        onlineEstimate: acc.onlineEstimate + d.onlineEstimate,
      }),
      { workDays: 0, leaveDays: 0, grossEstimate: 0, cashEstimate: 0, onlineEstimate: 0 }
    ),
    [periodData]
  );

  const rptPag = usePagination(periodData);

  const fmt = (n: number) => n.toLocaleString("en-IN", { style: "currency", currency: "INR", maximumFractionDigits: 0 });

  const exportReportCsv = () => {
    if (!currentPeriod) return;
    const header = "ID,Employee,Designation,Location,Working Days,Leave Days,Earned Holidays,Base Salary,Allowances,Gross Estimate,Cash,Online\n";
    const rows = periodData.map((d) =>
      `"${d.empId}","${d.empName}","${d.designation}","${d.location}",${d.workDays},${d.leaveDays},${d.earnedHolidays},${d.baseSalary},${d.totalAllowances.toFixed(0)},${d.grossEstimate.toFixed(0)},${d.cashEstimate.toFixed(0)},${d.onlineEstimate.toFixed(0)}`
    ).join("\n");
    const blob = new Blob([header + rows], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `report-${currentPeriod.label.replace(/\s/g, "-")}.csv`;
    link.click();
    URL.revokeObjectURL(url);
    setMsg("Report CSV downloaded.");
  };

  return (
    <Box>
      <Typography variant="h4" sx={{ mb: 1, fontWeight: 800 }}>
        Financial Reports
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Generate reports by financial year (April – March). View monthly, quarterly, half-yearly, or annual summaries.
      </Typography>

      {loading && <LinearProgress sx={{ mb: 2 }} />}

      {/* FY Selector & Location Filter */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
          <AssessmentIcon color="primary" />
          <Typography variant="h6">Report Settings</Typography>
        </Box>
        <Divider sx={{ mb: 2 }} />
        <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap", alignItems: "center" }}>
          <TextField
            type="number"
            label="FY Start Year"
            value={fyStart}
            onChange={(e) => setFyStart(parseInt(e.target.value) || dayjs().year())}
            sx={{ width: 140 }}
            helperText={fyLabel}
          />
          <FormControl sx={{ minWidth: 180 }}>
            <InputLabel>Location</InputLabel>
            <Select value={locationFilter} label="Location" onChange={(e: SelectChangeEvent) => setLocationFilter(e.target.value)}>
              <MenuItem value="">All Locations</MenuItem>
              {locations.map((l) => <MenuItem key={l.id} value={l.id}>{l.name}</MenuItem>)}
            </Select>
          </FormControl>
          <Button variant="outlined" startIcon={<DownloadIcon />} onClick={exportReportCsv}>
            Export CSV
          </Button>
        </Box>
        <Divider sx={{ my: 2 }} />
        <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap", alignItems: "center" }}>
          <Button
            size="small"
            variant={!useCustomRange ? "contained" : "outlined"}
            startIcon={<CalendarMonthIcon />}
            onClick={() => setUseCustomRange(false)}
            sx={{ textTransform: "none" }}
          >
            Financial Year
          </Button>
          <Button
            size="small"
            variant={useCustomRange ? "contained" : "outlined"}
            startIcon={<DateRangeIcon />}
            onClick={() => {
              setUseCustomRange(true);
              if (!rangeStart) setRangeStart(fyStartDate);
              if (!rangeEnd) setRangeEnd(fyEndDate);
            }}
            sx={{ textTransform: "none" }}
          >
            Custom Date Range
          </Button>
          {useCustomRange && (
            <>
              <DatePicker
                label="Start Date"
                value={rangeStart ? dayjs(rangeStart) : null}
                onChange={(v: Dayjs | null) => setRangeStart(v ? v.format("YYYY-MM-DD") : "")}
                format="DD/MMM/YYYY"
                slotProps={{ textField: { sx: { minWidth: 160 } } }}
              />
              <DatePicker
                label="End Date"
                value={rangeEnd ? dayjs(rangeEnd) : null}
                onChange={(v: Dayjs | null) => setRangeEnd(v ? v.format("YYYY-MM-DD") : "")}
                format="DD/MMM/YYYY"
                slotProps={{ textField: { sx: { minWidth: 160 } } }}
              />
            </>
          )}
        </Box>
      </Paper>

      {/* Period Tabs */}
      <Paper sx={{ overflow: "hidden", mb: 3 }}>
        <Tabs
          value={tab}
          onChange={(_, v) => setTab(v)}
          sx={{ borderBottom: 1, borderColor: "divider", px: 1, "& .MuiTab-root": { textTransform: "none", fontWeight: 600 } }}
        >
          <Tab icon={<CalendarMonthIcon />} iconPosition="start" label="Monthly" />
          <Tab label="Quarterly" />
          <Tab label="Half-Yearly" />
          <Tab label="Annual" />
        </Tabs>

        <Box sx={{ p: 3 }}>
          {/* Period selector (chips for months/quarters) */}
          {periods.length > 1 && (
            <Box sx={{ display: "flex", gap: 1, mb: 3, flexWrap: "wrap" }}>
              {periods.map((p, i) => (
                <Chip
                  key={p.label}
                  label={p.label}
                  onClick={() => setSelectedPeriod(i)}
                  color={selectedPeriod === i ? "primary" : "default"}
                  variant={selectedPeriod === i ? "filled" : "outlined"}
                  sx={{ fontWeight: 600 }}
                />
              ))}
            </Box>
          )}

          {/* Summary cards */}
          <Box sx={{ display: "flex", gap: 2, mb: 3, flexWrap: "wrap" }}>
            <SummaryCard icon={<PeopleIcon />} value={filteredEmployees.length} label="Employees" color="#F9A825" />
            <SummaryCard icon={<CalendarMonthIcon />} value={periodTotals.workDays} label="Total Work Days" color="#1565C0" />
            <SummaryCard icon={<CalendarMonthIcon />} value={periodTotals.leaveDays} label="Total Leave Days" color="#C62828" />
            <SummaryCard icon={<AssessmentIcon />} value={fmt(periodTotals.grossEstimate)} label="Gross Salary" color="#2E7D32" />
          </Box>

          {/* Period label */}
          {currentPeriod && (
            <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 2 }}>
              {currentPeriod.label} ({dayjs(currentPeriod.start).format("DD/MMM/YYYY")} to {dayjs(currentPeriod.end).format("DD/MMM/YYYY")})
            </Typography>
          )}

          {/* Data table */}
          <TableContainer sx={{ border: 1, borderColor: "divider" }}>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ bgcolor: "background.default" }}>
                  <TableCell sx={{ fontWeight: 700 }}>Employee</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Location</TableCell>
                  <TableCell align="center" sx={{ fontWeight: 700 }}>Work Days</TableCell>
                  <TableCell align="center" sx={{ fontWeight: 700 }}>Leave Days</TableCell>
                  <TableCell align="center" sx={{ fontWeight: 700 }}>Earned Hol.</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 700 }}>Allowances</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 700 }}>Gross Est.</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 700 }}>Cash</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 700 }}>Online</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {periodData.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={99} align="center" sx={{ py: 6, color: "text.secondary" }}>
                      <PeopleIcon sx={{ fontSize: 48, opacity: 0.3, mb: 1 }} /><br />
                      No data for this period.
                    </TableCell>
                  </TableRow>
                )}
                {rptPag.paginatedItems.map((d) => (
                  <TableRow key={d.empId} hover>
                    <TableCell>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>{d.empName}</Typography>
                      <Typography variant="caption" color="text.secondary">{d.designation}</Typography>
                    </TableCell>
                    <TableCell>{d.location}</TableCell>
                    <TableCell align="center">{d.workDays}</TableCell>
                    <TableCell align="center">{d.leaveDays}</TableCell>
                    <TableCell align="center">
                      <Chip label={`+${d.earnedHolidays}`} size="small" color="primary" variant="outlined" />
                    </TableCell>
                    <TableCell align="right">{fmt(d.totalAllowances)}</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 600 }}>{fmt(d.grossEstimate)}</TableCell>
                    <TableCell align="right" sx={{ color: "warning.dark" }}>{fmt(d.cashEstimate)}</TableCell>
                    <TableCell align="right" sx={{ color: "secondary.dark" }}>{fmt(d.onlineEstimate)}</TableCell>
                  </TableRow>
                ))}
                {periodData.length > 0 && (
                  <TableRow sx={{ bgcolor: (t) => t.palette.mode === "light" ? "#e8f5e9" : "rgba(46,125,50,0.15)" }}>
                    <TableCell colSpan={2} sx={{ fontWeight: 800 }}>TOTALS</TableCell>
                    <TableCell align="center" sx={{ fontWeight: 800 }}>{periodTotals.workDays}</TableCell>
                    <TableCell align="center" sx={{ fontWeight: 800 }}>{periodTotals.leaveDays}</TableCell>
                    <TableCell />
                    <TableCell />
                    <TableCell align="right" sx={{ fontWeight: 800 }}>{fmt(periodTotals.grossEstimate)}</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 800 }}>{fmt(periodTotals.cashEstimate)}</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 800 }}>{fmt(periodTotals.onlineEstimate)}</TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
          {periodData.length > 0 && (
            <TablePagination component="div" count={rptPag.totalCount} page={rptPag.page} onPageChange={rptPag.onPageChange} rowsPerPage={rptPag.rowsPerPage} onRowsPerPageChange={rptPag.onRowsPerPageChange} rowsPerPageOptions={[10, 25, 50]} />
          )}
        </Box>
      </Paper>

      <Snackbar open={!!msg} autoHideDuration={4000} onClose={() => setMsg("")} anchorOrigin={{ vertical: "bottom", horizontal: "right" }}>
        <Alert severity="info" variant="filled" onClose={() => setMsg("")}>{msg}</Alert>
      </Snackbar>
    </Box>
  );
}
